<script lang="ts" setup>
import { getPayrollData } from '@/app/core/services/payrolls'
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import { useField, useForm } from 'vee-validate'
import { object, string, array, date } from 'yup'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DMultiselect,
  DEmployeesFilter,
  DModal,
  DLoading,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  ProcessRequests,
  createControlCalculate,
  getCalendars,
  getEmployees,
  getConcepts,
  getProcesses
} from '@/app/core/services/pending-processes'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const storePayroll = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Propiedades Computadas */
const lastProcessDate = computed(() => {
  return codeProcess() === 'N' //Nomina
    ? payroll.value.lastPayment
    : codeProcess() === 'U' //Utilidades
    ? payroll.value.lastBonus
    : ''
})

/* Codigo proceso */
const codeProcess = () => {
  if (processId.value !== undefined) {
    return itemsProcesses.value.filter((_: any) => _.id === processId.value)[0][
      'code'
    ]
  }
}

/* Datos */
const itemsProcesses = ref([])
let allItemsConcepts: any[] = []
const itemsConcepts = ref()
const itemsEmployees = ref<string[]>([])
const payroll = ref()
const conceptNet = ref()
const emptyCalendar = ref<boolean>(false)

/* Datos componente DModal y DLoading */
const showModal = ref(false)
const typeAlertModal = ref('')
const titleModal = ref('')
const descriptionModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  processId: string().required(
    t('form.valid.fieldRequired', { field: t('fields.processId') })
  ),
  concepts: array()
    .min(1, t('pendingProcesses.valid.selectAtLeastOneConcept'))
    .required(t('form.valid.fieldRequired', { field: t('fields.conceptId') })),
  startDate: date().required(
    t('form.valid.fieldRequired', { field: t('fields.startDate') })
  ),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.fieldRequired', { field: t('fields.endDate') }))
    }
    return schema.required(
      t('form.valid.fieldRequired', { field: t('fields.endDate') })
    )
  }),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employeeId') }))
})

/* Uso del metodo de validaión, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<ProcessRequests>({
  validationSchema
})

/* Definicion de  campos del formulario */
const { value: processId } = useField<string>(ref('processId'))
const { value: concepts } = useField<any[]>(ref('concepts'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>(ref('endDate'))
const { value: employees } = useField<any[]>(ref('employees'))
watch(endDate, () => {
  void listEmployees(endDate.value)
})

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any, { resetForm }) => {
  if (
    conceptNet.value !== null &&
    conceptNet.value !== '' &&
    conceptNet.value !== undefined
  ) {
    values.concepts.push(conceptNet.value)
  }
  void createControlCalculation(values)
})

/* Crear control de calculo */
const createControlCalculation = async (values: any) => {
  let saved = false
  const process: any = itemsProcesses.value.filter(
    (process: any) => process.id === processId.value
  )[0]
  loading.value = true
  messageLoading.value = ''
  try {
    const result = await createControlCalculate(values)
    if (result?.response?.status >= 200 && result?.response?.status < 300) {
      saved = true
    } else {
      saved = false
    }
    if (saved) {
      loading.value = false
      notifications.addNotification({
        title: t('notification.created'),
        text: t('pendingProcesses.notification.create', {
          payroll: payroll.value.name as string,
          startDate: dayjs(startDate.value).format('DD-MM-YYYY'),
          endDate: dayjs(endDate.value).format('DD-MM-YYYY'),
          process: process.name as string
        }),
        type: 'success',
        time: 10000
      })
    } else {
      loading.value = false
      notificationError(result, 'pendingProcesses', t, notifications, loading)
    }
  } catch (error: any) {
    notificationError(error, 'pendingProcesses', t, notifications, loading)
  }
}

/* Filtrar procesos */
const filterProcesses = async () => {
  let processes = await getProcesses()
  processes = processes.filter(
    (_: any) =>
      (_.countries?.length <= 0 ||
      _.countries.indexOf(storePayroll.payroll?.country?.id) > -1 ) &&
      _.code != 'L'
  )
  itemsProcesses.value = processes
}

/* Filtrar conceptos */
const filterConcepts = (idProcess: any) => {
  concepts.value = []
  let itemsFilter: any[] = []
  itemsFilter = [...allItemsConcepts]
  itemsFilter = itemsFilter.filter(
    (_: any) => _.processes.indexOf(idProcess) > -1 && _.id !== conceptNet.value
  )
  itemsConcepts.value = itemsFilter
  concepts.value = itemsFilter.map((_: any) => _.id.toString())
  void getCalendarPayroll()
}

const initialDate = () => {
  startDate.value = dayjs().format('YYYY-MM-DD')
  endDate.value = dayjs().format('YYYY-MM-DD')
}

const getCalendarPayroll = async () => {
  if (codeProcess() === 'N') {
    if (
      payroll.value.lastPayment === null &&
      payroll.value.lastPayment === ''
    ) {
      initialDate()
    } else {
      const dateCalendar = await getCalendars(payroll.value.lastPayment)
      if (!dateCalendar) {
        emptyCalendar.value = true
        notifications.addNotification({
          title: t('pendingProcesses.notification.noCalendars'),
          text: t('pendingProcesses.notification.noAssociatedCalendars'),
          type: 'error',
          time: 10000
        })
      } else {
        startDate.value = dateCalendar.initialDate
        endDate.value = dateCalendar.finalDate
      }
    }
  } else {
    emptyCalendar.value = false
    initialDate()
  }
}

const listEmployees = async (dateEnd: any) => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    itemsEmployees.value = await getEmployees(dateEnd)
    employees.value = itemsEmployees.value.map((_: any) => _.id)
    loading.value = false
  } catch (error) {
    loading.value = false
  }
}

/* Validar campos de fechas (no dejar vacios) */
const validateDate = () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

onMounted(async () => {
  loading.value = true
  try {
    payroll.value = await getPayrollData(storePayroll?.payroll?.id ?? '')
    await filterProcesses()
    allItemsConcepts = await getConcepts()
    conceptNet.value = [...allItemsConcepts]
    conceptNet.value = conceptNet.value
      .filter((_: any) => _.code.includes('999999'))[0]
      ?.id.toString()
    void getCalendarPayroll()
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('pendingProcesses.notification.error'),
        type: 'error',
        time: 10000
      })
      void router.push({ name: 'Home' })
    } else {
      notificationError(error, 'pendingProcesses', t, notifications, loading)
    }
  }
})
</script>
<template>
  <div class="h-full px-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal" />
    <div class="bg-gray-50 pb-10">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('pendingProcesses.create.title')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 12rem)">
      <div class="flex flex-col gap-5">
        <DCard>
          <div class="grid grid-cols-3">
            <div class="col-span-4 lg:col-span-2 py-1 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                  <DSelectFieldSearch
                    :label="t('pendingProcesses.fields.process')"
                    v-model="processId"
                    :options="itemsProcesses"
                    @change="filterConcepts(processId)"
                    :error="errors.processId" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                  <DMultiselect
                    :label="t('pendingProcesses.fields.concepts')"
                    v-model="concepts"
                    :headerTitle="t('pendingProcesses.fields.concepts')"
                    :options="itemsConcepts"
                    :error="errors.concepts"
                    :disabled="codeProcess() === 'N'" />
                </div>
                <hr class="col-span-6" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                  <DTextField
                    type="date"
                    :label="t('pendingProcesses.fields.startDate')"
                    v-model="startDate"
                    :disabled="
                      codeProcess() === 'N' &&
                      payroll.lastPayment != null &&
                      payroll.lastPayment != ''
                    "
                    :error="errors.startDate"
                    @input="validateDate" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                  <DTextField
                    type="date"
                    :label="t('pendingProcesses.fields.endDate')"
                    v-model="endDate"
                    :disabled="
                      codeProcess() === 'N' &&
                      payroll.lastPayment != null &&
                      payroll.lastPayment != ''
                    "
                    :error="errors.endDate"
                    @input="validateDate" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="px-4 flex justify-between">
                <h3 class="text-md font-medium leading-6 text-gray-900">
                  {{ t('pendingProcesses.fields.employees') }}
                </h3>
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div class="flex justify-center items-center p-5 gap-5">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
        </DCard>
        <DCard>
          <div class="grid grid-cols-4 items-center">
            <div class="col-span-4 lg:col-span-1 px-4">
              <h3 class="text-lg font-medium leading-6 text-gray-900">
                {{
                  `${t('pendingProcesses.fields.lastProcess')}: ${
                    !lastProcessDate
                      ? ''
                      : dayjs(lastProcessDate).format('DD-MM-YYYY')
                  }`
                }}
              </h3>
            </div>
            <div class="col-span-4 lg:col-span-1 px-4">
              <h3 class="text-lg font-medium leading-6 text-gray-900">
                {{
                  `${t('pendingProcesses.fields.employees')}: ${
                    !employees?.length ? 0 : employees?.length
                  }/${itemsEmployees?.length}`
                }}
              </h3>
            </div>
            <div class="col-span-4 lg:col-span-1 px-4">
              <h3 class="text-lg font-medium leading-6 text-gray-900">
                {{
                  `${t('pendingProcesses.fields.concepts')}: ${
                    !concepts?.length ? 0 : concepts.length
                  } / ${!itemsConcepts?.length ? 0 : itemsConcepts?.length}`
                }}
              </h3>
            </div>
            <div class="col-span-4 lg:col-span-1 px-4">
              <DBtn
                :disabled="emptyCalendar"
                class="w-full"
                @click="onSubmit"
                v-if="store.hasPermission('calc_controller:execute')"
                v-html="t('form.button.add')" />
            </div>
          </div>
        </DCard>
      </div>
    </form>
  </div>
</template>
